// src/hooks/useDocumentPolling.js

import { useEffect, useRef } from 'react';
import { getDocProgress } from '../services/docServices';
import { isMobileDevice, isPageHidden } from '../utils/mobileOptimizations';

/**
 * Hook to handle polling for document processing status
 * @param {Array} projects - The projects array containing docs to poll
 * @param {Function} updateDocInProjects - Function to update doc in projects state
 * @param {Function} getKbIdForFile - Function to get kb_id for a file
 * @param {number} pollInterval - Polling interval in ms (default: 10000ms = 10s)
 */
export function useDocumentPolling({
  projects,
  updateDocInProjects,
  getKbIdForFile,
  pollInterval = 10000, // 10s for all devices (mobile and desktop)
  maxPollDuration = 1800000 // Stop polling after 30 minutes (handles large documents)
}) {
  // Keep track of active intervals to clean them up
  const intervalMapRef = useRef({});
  // Track documents we've seen to detect deletions
  const trackedDocsRef = useRef(new Set());
  // Track poll count per document (use force=true only occasionally, not every time)
  const pollCountRef = useRef({});
  // Track when polling started for each document
  const pollStartTimeRef = useRef({});
  
  // Collect all files that need polling (run=1)
  useEffect(() => {
    const activePollingFiles = new Map(); // Use Map to prevent duplicates
    const currentDocIds = new Set();
    
    // Find all files that need polling across all projects
    for (const project of projects) {
      for (const phase of project.phases) {
        if (!phase.kb_id) continue;
        
        for (const file of phase.files) {
          // Add to current docs set
          currentDocIds.add(file.id);
          
          // If file is running, add to polling list (Map prevents duplicates)
          if (file.run === '1' && !activePollingFiles.has(file.id)) {
            activePollingFiles.set(file.id, {
              id: file.id,
              kbId: phase.kb_id
            });
          }
        }
      }
    }
    
    // Log if we found any documents to poll
    if (activePollingFiles.size > 0) {
      console.log(`[DocPolling] Found ${activePollingFiles.size} unique document(s) needing polling (run="1")`);
    }
    
    // Clean up intervals for docs that are no longer present (e.g., deleted)
    Object.keys(intervalMapRef.current).forEach(fileId => {
      if (!currentDocIds.has(fileId)) {
        console.log(`[DocPolling] Document ${fileId} no longer exists, stopping polling`);
        clearInterval(intervalMapRef.current[fileId]);
        delete intervalMapRef.current[fileId];
        delete pollCountRef.current[fileId]; // Clean up poll counter
        delete pollStartTimeRef.current[fileId]; // Clean up start time
      }
    });
    
    // Start new intervals for files that need polling
    for (const file of activePollingFiles.values()) {
      // Skip if already polling
      if (intervalMapRef.current[file.id]) continue;
      
      console.log(`[DocPolling] Starting polling for document ${file.id}`);
      
      // Record start time for this document
      pollStartTimeRef.current[file.id] = Date.now();
      
      // Start a new interval
      const interval = setInterval(async () => {
        // Check if we've been polling too long (stuck document)
        const pollingDuration = Date.now() - pollStartTimeRef.current[file.id];
        if (pollingDuration > maxPollDuration) {
          console.warn(`[DocPolling] Document ${file.id} has been polling for ${Math.round(pollingDuration / 60000)}min (> 30min), stopping`);
          clearInterval(intervalMapRef.current[file.id]);
          delete intervalMapRef.current[file.id];
          delete pollCountRef.current[file.id];
          delete pollStartTimeRef.current[file.id];
          return;
        }
        await pollFileProgress(file.id, file.kbId);
      }, pollInterval);
      
      // Save the interval reference
      intervalMapRef.current[file.id] = interval;
      
      // Poll immediately
      pollFileProgress(file.id, file.kbId);
    }
    
    // Update tracked docs
    trackedDocsRef.current = currentDocIds;
    
    // Cleanup function
    return () => {
      Object.values(intervalMapRef.current).forEach(clearInterval);
      intervalMapRef.current = {};
    };
  }, [projects, updateDocInProjects, getKbIdForFile, pollInterval]);
  
  // Function to poll a single file's progress
  async function pollFileProgress(fileId, kbId) {
    // Don't poll when page is hidden to save resources
    if (isPageHidden()) {
      return;
    }
    
    try {
      // Get kb_id either from passed value or from lookup function
      const effectiveKbId = kbId || getKbIdForFile(fileId);
      
      if (!effectiveKbId) {
        console.warn(`[DocPolling] No KB ID found for file ${fileId}, stopping polling`);
        if (intervalMapRef.current[fileId]) {
          clearInterval(intervalMapRef.current[fileId]);
          delete intervalMapRef.current[fileId];
        }
        return;
      }
      
      // Check if the document still exists
      if (!trackedDocsRef.current.has(fileId)) {
        console.log(`[DocPolling] Document ${fileId} was deleted, stopping polling`);
        if (intervalMapRef.current[fileId]) {
          clearInterval(intervalMapRef.current[fileId]);
          delete intervalMapRef.current[fileId];
        }
        return;
      }
      
      // Get the doc progress from server
      // Use force=true only occasionally (every 10th poll) to reduce backend load
      // This prevents overwhelming the database with cache-busting queries
      pollCountRef.current[fileId] = (pollCountRef.current[fileId] || 0) + 1;
      const useForce = pollCountRef.current[fileId] % 10 === 0; // force=true every 10th poll (~50s)
      const updatedDoc = await getDocProgress(fileId, effectiveKbId, useForce);
      
      if (!updatedDoc) {
        console.warn(`[DocPolling] No doc found for ID ${fileId}, may be deleted`);
        // Force a final check with force=true parameter
        const finalCheck = await getDocProgress(fileId, effectiveKbId, true);
        
        if (!finalCheck) {
          console.log(`[DocPolling] Document ${fileId} confirmed missing, stopping polling`);
          if (intervalMapRef.current[fileId]) {
            clearInterval(intervalMapRef.current[fileId]);
            delete intervalMapRef.current[fileId];
          }
        }
        return;
      }
      
      // If doc is finished, stop polling
      // Check: run="3" (done), run="0" (unstarted), run="4" (failed), or progress=100%
      const isComplete = 
        updatedDoc.run === '3' ||  // DONE - Graph RAG/parsing complete
        updatedDoc.run === '0' ||  // UNSTART - not queued
        updatedDoc.run === '4' ||  // FAIL - error occurred
        parseFloat(updatedDoc.progress) >= 1.0;
      
      if (isComplete) {
        console.log(`[DocPolling] Document ${fileId} processing complete (run=${updatedDoc.run}, progress=${updatedDoc.progress}), stopping polling`);
        if (intervalMapRef.current[fileId]) {
          clearInterval(intervalMapRef.current[fileId]);
          delete intervalMapRef.current[fileId];
        }
        // Clean up poll counter and start time
        delete pollCountRef.current[fileId];
        delete pollStartTimeRef.current[fileId];
      }
      
      // Update the doc in projects
      updateDocInProjects(fileId, updatedDoc);
    } catch (err) {
      console.error(`[DocPolling] Error polling progress for doc ${fileId}:`, err);
    }
  }
  
  // Return API for external control if needed
  return {
    // Function to manually start polling for a file
    startPolling: (fileId, kbId) => {
      if (!kbId && !getKbIdForFile) {
        console.error('Cannot start polling without kb_id');
        return;
      }
      
      if (intervalMapRef.current[fileId]) {
        // Already polling
        return;
      }
      
      const effectiveKbId = kbId || getKbIdForFile(fileId);
      if (!effectiveKbId) {
        console.error('KB ID not found for file', fileId);
        return;
      }
      
      // Add to tracked docs
      trackedDocsRef.current.add(fileId);
      
      console.log(`[DocPolling] Manually starting polling for document ${fileId}`);
      
      // Start a new interval
      const interval = setInterval(() => {
        pollFileProgress(fileId, effectiveKbId);
      }, pollInterval);
      
      // Save the interval reference
      intervalMapRef.current[fileId] = interval;
      
      // Poll immediately
      pollFileProgress(fileId, effectiveKbId);
    },
    
    // Function to manually stop polling for a file
    stopPolling: (fileId) => {
      if (intervalMapRef.current[fileId]) {
        console.log(`[DocPolling] Manually stopping polling for document ${fileId}`);
        clearInterval(intervalMapRef.current[fileId]);
        delete intervalMapRef.current[fileId];
      }
    }
  };
} 